import { Injectable } from '@angular/core';
import { INameIdObj, INameIdTypeObjWithChildren } from '@fi-shared/fi-shared-interfaces';
import {
    IChecklist2ForReport,
    IChecklist2ForReportWithChildren,
    IChecklist2ReportChecklistSummary,
    IChecklistScheduleForReport,
    IChecklistV2FilterAvailableItems,
    ICheckpoint2GroupForReport,
    ICheckpointV2ForReport,
} from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report';
import { ObjectTypes } from '@fi-shared/types';
import { IFilterMenuCreateArgs } from '@fi-shared/checklist/fi-checklist2-report/components/shared/widget-table-filter-helper.service';
import { cloneDeep, isDefined } from '@fi-shared/utils';
import { WidgetTableFilterType } from '@fi-shared/checklist/fi-checklist2-report/enums/fi-checklist2-report-type';
import { FiChecklist2ReportData } from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report-data';
import { ISharingMenuData } from '@fi-shared/fi-items-list/fi-items-list-filter/fi-items-list-filter/fi-items-list-filter';
import { FiFilterType } from '@fi-shared/fi-items-list/fi-items-list-filter/fi-filter-type-enum';

@Injectable()
export class ChecklistWidgetTableFilterDataService {
    constructor() {}

    isHistoryReport(widgetTableFilterType: WidgetTableFilterType) {
        return [
            WidgetTableFilterType.History,
            WidgetTableFilterType.UnitBenchmarkByChecklists,
            WidgetTableFilterType.UnitBenchmarkByCheckpointGroups,
            WidgetTableFilterType.UnitBenchmarkByCheckpoints,
        ].includes(widgetTableFilterType);
    }

    isAreaAvailableByFilterType(widgetTableFilterType: WidgetTableFilterType) {
        return ![
            WidgetTableFilterType.UnitBenchmarkByChecklists,
            WidgetTableFilterType.UnitBenchmarkByCheckpointGroups,
            WidgetTableFilterType.UnitBenchmarkByCheckpoints,
        ].includes(widgetTableFilterType);
    }

    isRecentActivityWidget(widgetTableFilterType: WidgetTableFilterType) {
        return widgetTableFilterType === WidgetTableFilterType.RecentActivity;
    }

    isAttachmentsList(widgetTableFilterType: WidgetTableFilterType) {
        return (
            widgetTableFilterType === WidgetTableFilterType.ChecklistHistoryAttachmentsList ||
            widgetTableFilterType === WidgetTableFilterType.ChecklistOpenAttachmentsList
        );
    }

    isHistoryAttachmentsList(widgetTableFilterType: WidgetTableFilterType) {
        return widgetTableFilterType === WidgetTableFilterType.ChecklistHistoryAttachmentsList;
    }

    //# region Checklists
    isChecklistsAndSchedulesMenu(args: IFilterMenuCreateArgs) {
        const widgetTableFilterType = args.widgetTableFilterTypeFn();
        return (
            this.isHistoryReport(widgetTableFilterType) ||
            this.isRecentActivityWidget(widgetTableFilterType) ||
            this.isHistoryAttachmentsList(widgetTableFilterType)
        );
    }

    getChecklistMenuItems(args: IFilterMenuCreateArgs): IChecklist2ForReport[] {
        const items = this.isChecklistsAndSchedulesMenu(args)
            ? this.getHistoryChecklistsOptionsFilterData(args.availableItems, args.availableChecklistScheduleIdsByData)
            : args.availableItems.Checklists;
        return items;
    }

    public getHistoryChecklistsOptionsFilterData(
        availableItems: IChecklistV2FilterAvailableItems,
        availableChecklistScheduleIdsByData: number[],
    ): IChecklist2ForReport[] {
        const checklists: IChecklist2ForReport[] = availableItems.Checklists.map(
            (availableChecklist: IChecklist2ForReport) => {
                const checklistClone: IChecklist2ForReportWithChildren<IChecklistScheduleForReport> =
                    cloneDeep(availableChecklist);
                const relatedScheduleIds = checklistClone.ChecklistScheduleIds.filter(
                    (checklistScheduleId) =>
                        !availableChecklistScheduleIdsByData ||
                        availableChecklistScheduleIdsByData.includes(checklistScheduleId),
                );

                if (!relatedScheduleIds?.length) return undefined;

                checklistClone.ChildItems = availableItems.Schedules.filter(
                    (scheduleForReport: IChecklistScheduleForReport) => {
                        return relatedScheduleIds.includes(scheduleForReport.Id);
                    },
                );
                return checklistClone;
            },
        ).filter((item: IChecklist2ForReportWithChildren<IChecklistScheduleForReport>) => isDefined(item));

        return checklists;
    }

    getRelatedSchedulesIds(checklistId: number, availableChecklists: IChecklist2ForReport[]): number[] {
        const checklist = availableChecklists.find((checklist) => checklist.Id === checklistId);
        return checklist.ChecklistScheduleIds;
    }

    //# endregion

    //# region Checkpoints and Checkpoint groups
    getCheckpointMenuItemsForMenu(
        selectedChecklistIds: number[],
        checklists: IChecklist2ForReport[],
        checkpointGroups: ICheckpoint2GroupForReport[],
        checkpoints: ICheckpointV2ForReport[],
    ): INameIdTypeObjWithChildren[] {
        // build 3 level depth structure. 1st level checklists, 2nd checkpoint groups, 3rd checkpoints
        const items: INameIdTypeObjWithChildren[] = checklists
            .filter((checklist) => !selectedChecklistIds || selectedChecklistIds.includes(checklist.Id))
            .map((checklist) => {
                return this.mapNameIdTypeObjWithChildren(
                    checklist,
                    ObjectTypes.Checklist2,
                    (id: number, objectType: ObjectTypes) => {
                        if (objectType === ObjectTypes.Checklist2)
                            return checkpointGroups.filter((checkpointGroup) => checkpointGroup.ChecklistId === id);
                        if (objectType === ObjectTypes.CheckpointGroup)
                            return checkpoints.filter((checkpoint) => checkpoint.CheckpointGroupId === id);
                        return [];
                    },
                    (objectType) => {
                        if (objectType === ObjectTypes.Checklist2) return ObjectTypes.CheckpointGroup;
                        if (objectType === ObjectTypes.CheckpointGroup) return ObjectTypes.Checkpoint2;
                        throw new Error(`getCheckpointMenuItemsForMenu: unknown object type ${objectType}`);
                    },
                    (objectType) => {
                        if (objectType === ObjectTypes.Checklist2) return 0;
                        if (objectType === ObjectTypes.CheckpointGroup) return 1;
                        if (objectType === ObjectTypes.Checkpoint2) return 2;
                        throw new Error(`getCheckpointMenuItemsForMenu: unknown object type ${objectType}`);
                    },
                );
            });
        return items;
    }

    private mapNameIdTypeObjWithChildren(
        item: INameIdObj,
        objectType: ObjectTypes,
        getChildItemsFn: (id: number, objectType: ObjectTypes) => INameIdObj[],
        getChildItemObjectTypeFn: (objectType: ObjectTypes) => ObjectTypes,
        getLevelFn: (objectType: ObjectTypes) => number,
    ): INameIdTypeObjWithChildren {
        const result: INameIdTypeObjWithChildren = {
            Id: item.Id,
            Name: item.Name,
            ObjectType: objectType,
            Level: getLevelFn(objectType),
            ChildItems: getChildItemsFn(item.Id, objectType).map((childItem) =>
                this.mapNameIdTypeObjWithChildren(
                    childItem,
                    getChildItemObjectTypeFn(objectType),
                    getChildItemsFn,
                    getChildItemObjectTypeFn,
                    getLevelFn,
                ),
            ),
        };
        return result;
    }
    //# endregion
}
